import {
  groomingQueueActionResultSchema,
  groomingQueueStateSchema,
  type GroomingEngineErrorCode,
  type GroomingQueueActionResult,
  type GroomingQueueState,
  type GroomingToolsetBridge,
  type ToolboxBridge,
} from "@ttc-spectree/toolbox-contract";
import { plainBridgeError } from "@/lib/toolbox/bridge-error";

/**
 * The grooming run queue's half of the SpecTree Toolbox bridge, for
 * SpecTree's pages (epic PI-SPEC-SW-E284, feature PI-SPEC-SW-1439).
 *
 * The queue lives on the person's computer: SpecTree Toolbox keeps it, saves
 * it to disk and runs it on the person's own Claude Code seat. The pages only
 * render what the app reports (`grooming.getQueue()` and `queue-changed`
 * events) and send actions through the bridge. There is no SpecTree API call
 * for queue state.
 *
 * The queue arrived with grooming contract 3: every queue method is optional
 * on the bridge, so a page uses it only when {@link queueSupported} is true.
 * Every result crosses the app/page version boundary and is parsed with the
 * contract's schemas; a result the page cannot read becomes a failed result
 * instead of throwing. The shapes come from `@ttc-spectree/toolbox-contract`;
 * never re-type them here.
 */

/** The first grooming contract number that ships the run queue. */
export const GROOMING_QUEUE_CONTRACT = 3;

/** The grooming bridge narrowed to a contract-3 app: `getQueue` is present. */
export type QueueBridge = GroomingToolsetBridge & {
  getQueue: NonNullable<GroomingToolsetBridge["getQueue"]>;
};

/**
 * Whether the page may use the run queue: inside SpecTree Toolbox, with a
 * grooming contract of 3 or later and the queue methods on the bridge.
 * False in a browser and with a contract-2 app.
 */
export function queueSupported(bridge: ToolboxBridge | null | undefined): boolean {
  const contract = bridge?.app.contracts.grooming;
  return (
    contract !== undefined &&
    contract >= GROOMING_QUEUE_CONTRACT &&
    typeof bridge?.grooming?.getQueue === "function"
  );
}

/** The grooming bridge when the queue is supported, else null. */
export function queueBridgeOf(bridge: ToolboxBridge | null | undefined): QueueBridge | null {
  if (!queueSupported(bridge)) return null;
  return (bridge?.grooming as QueueBridge | undefined) ?? null;
}

/** A failed queue action the page builds itself (method missing, call rejected, result unreadable). */
function failure(message: string, errorCode: GroomingEngineErrorCode = "UNKNOWN"): GroomingQueueActionResult {
  return { ok: false, errorCode, message };
}

/** Parse what the app returned for an action; an unreadable result is a failure, never a throw. */
export function parseQueueActionResult(raw: unknown): GroomingQueueActionResult {
  const parsed = groomingQueueActionResultSchema.safeParse(raw);
  return parsed.success ? parsed.data : failure("SpecTree Toolbox sent a result this page cannot read.");
}

/** Parse a queue state from the app; null when it does not match the contract. */
export function parseQueueState(raw: unknown): GroomingQueueState | null {
  const parsed = groomingQueueStateSchema.safeParse(raw);
  return parsed.success ? parsed.data : null;
}

/** A rejected bridge call as plain text: Electron's "Error invoking remote method" prefix removed. */
function errorMessage(error: unknown): string {
  return plainBridgeError(error, "SpecTree Toolbox could not do that.");
}

/**
 * Run one bridge action. `invoke` calls the optional method with `?.`, so it
 * returns undefined when a contract-2 app lacks the method.
 */
async function call(invoke: () => Promise<unknown> | undefined): Promise<GroomingQueueActionResult> {
  let pending: Promise<unknown> | undefined;
  try {
    pending = invoke();
  } catch (error) {
    return failure(errorMessage(error));
  }
  if (pending === undefined) return failure("Update SpecTree Toolbox to line up grooming runs.");
  try {
    return parseQueueActionResult(await pending);
  } catch (error) {
    return failure(errorMessage(error));
  }
}

/** `grooming.getQueue()`, parsed; null when the method is missing, rejects or returns something unreadable. */
export async function getQueue(grooming: GroomingToolsetBridge): Promise<GroomingQueueState | null> {
  if (typeof grooming.getQueue !== "function") return null;
  try {
    return parseQueueState(await grooming.getQueue());
  } catch {
    return null;
  }
}

/** `grooming.enqueueItem(project, itemId)`: queue one tracker item for a brief. */
export function enqueueItem(grooming: GroomingToolsetBridge, project: string, itemId: string) {
  return call(() => grooming.enqueueItem?.(project, itemId));
}

/** `grooming.enqueueBatch(batchId)`: queue every remaining item of a draft batch. */
export function enqueueBatch(grooming: GroomingToolsetBridge, batchId: string) {
  return call(() => grooming.enqueueBatch?.(batchId));
}

/** `grooming.enqueueProductAreas(project)`: queue a draft of the project's product areas. */
export function enqueueProductAreas(grooming: GroomingToolsetBridge, project: string) {
  return call(() => grooming.enqueueProductAreas?.(project));
}

/** `grooming.enqueueAuthorQuestions(project, itemId)`: queue a draft of questions for the item's author. */
export function enqueueAuthorQuestions(grooming: GroomingToolsetBridge, project: string, itemId: string) {
  return call(() => grooming.enqueueAuthorQuestions?.(project, itemId));
}

/** `grooming.removeFromQueue(entryId)`: remove a queued (not running) entry. */
export function removeFromQueue(grooming: GroomingToolsetBridge, entryId: string) {
  return call(() => grooming.removeFromQueue?.(entryId));
}

/** `grooming.moveInQueue(entryId, toIndex)`: move a queued entry within Up next (0 = next to run). */
export function moveInQueue(grooming: GroomingToolsetBridge, entryId: string, toIndex: number) {
  return call(() => grooming.moveInQueue?.(entryId, toIndex));
}

/** `grooming.stopQueueEntry(entryId)`: stop one running entry. */
export function stopQueueEntry(grooming: GroomingToolsetBridge, entryId: string) {
  return call(() => grooming.stopQueueEntry?.(entryId));
}

/** `grooming.stopQueue()`: stop every running entry and pause the queue. */
export function stopQueue(grooming: GroomingToolsetBridge) {
  return call(() => grooming.stopQueue?.());
}

/** `grooming.retryQueueEntry(entryId)`: queue a failed or stopped entry again. */
export function retryQueueEntry(grooming: GroomingToolsetBridge, entryId: string) {
  return call(() => grooming.retryQueueEntry?.(entryId));
}

/** `grooming.resumeQueue()`: resume a paused queue. */
export function resumeQueue(grooming: GroomingToolsetBridge) {
  return call(() => grooming.resumeQueue?.());
}

/** `grooming.clearCompletedQueue()`: clear the Completed list. */
export function clearCompletedQueue(grooming: GroomingToolsetBridge) {
  return call(() => grooming.clearCompletedQueue?.());
}

/** `grooming.setQueueConcurrency(n)`: how many entries run at once (within `GROOMING_QUEUE_CONCURRENCY`). */
export function setQueueConcurrency(grooming: GroomingToolsetBridge, n: number) {
  return call(() => grooming.setQueueConcurrency?.(n));
}

/**
 * `grooming.refreshUsage()` (optional, PKG-PM-C Mac walk 2026-10-23): ask the
 * app to refresh a usage reading that is not current. The new reading arrives
 * as `queue-changed`; `onRejected` runs when the call itself fails (the app
 * may then publish no `usageCheck`, so the caller records the failure).
 * False when the app has no such call.
 */
export function refreshUsage(grooming: GroomingToolsetBridge, onRejected?: () => void): boolean {
  if (typeof grooming.refreshUsage !== "function") return false;
  try {
    void grooming.refreshUsage().catch(() => {
      onRejected?.();
    });
  } catch {
    return false;
  }
  return true;
}

/** Every queue action, bound to one bridge. `getQueue` is read through the store (see `useGroomingQueue`). */
export interface GroomingQueueActions {
  enqueueItem(project: string, itemId: string): Promise<GroomingQueueActionResult>;
  enqueueBatch(batchId: string): Promise<GroomingQueueActionResult>;
  enqueueProductAreas(project: string): Promise<GroomingQueueActionResult>;
  enqueueAuthorQuestions(project: string, itemId: string): Promise<GroomingQueueActionResult>;
  remove(entryId: string): Promise<GroomingQueueActionResult>;
  move(entryId: string, toIndex: number): Promise<GroomingQueueActionResult>;
  stop(entryId: string): Promise<GroomingQueueActionResult>;
  stopAll(): Promise<GroomingQueueActionResult>;
  retry(entryId: string): Promise<GroomingQueueActionResult>;
  resume(): Promise<GroomingQueueActionResult>;
  clearCompleted(): Promise<GroomingQueueActionResult>;
  setConcurrency(n: number): Promise<GroomingQueueActionResult>;
  /** True when the app has `refreshUsage` (an older contract-3 app has not). */
  canRefreshUsage: boolean;
  /** Ask the app to refresh the usage reading; false when it cannot. `onRejected` runs when the call fails. */
  refreshUsage: (onRejected?: () => void) => boolean;
}

/** Bind the queue actions to one grooming bridge. */
export function queueActionsFor(grooming: GroomingToolsetBridge): GroomingQueueActions {
  return {
    enqueueItem: (project, itemId) => enqueueItem(grooming, project, itemId),
    enqueueBatch: (batchId) => enqueueBatch(grooming, batchId),
    enqueueProductAreas: (project) => enqueueProductAreas(grooming, project),
    enqueueAuthorQuestions: (project, itemId) => enqueueAuthorQuestions(grooming, project, itemId),
    remove: (entryId) => removeFromQueue(grooming, entryId),
    move: (entryId, toIndex) => moveInQueue(grooming, entryId, toIndex),
    stop: (entryId) => stopQueueEntry(grooming, entryId),
    stopAll: () => stopQueue(grooming),
    retry: (entryId) => retryQueueEntry(grooming, entryId),
    resume: () => resumeQueue(grooming),
    clearCompleted: () => clearCompletedQueue(grooming),
    setConcurrency: (n) => setQueueConcurrency(grooming, n),
    canRefreshUsage: typeof grooming.refreshUsage === "function",
    refreshUsage: (onRejected) => refreshUsage(grooming, onRejected),
  };
}

/** Actions for a page that has no queue: every call resolves as a failure. */
export const UNSUPPORTED_QUEUE_ACTIONS: GroomingQueueActions = queueActionsFor({} as GroomingToolsetBridge);
